import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    Lock as LockIcon,
    Remove as RemoveIcon,
    ShoppingBag as ShoppingBagIcon,
    VerifiedUser as VerifiedUserIcon
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { createCreditShopCheckout, createMontonioShopCheckout } from "../api/shopApi";
import { getUserCredit } from "../api/planApi";
import { formatCurrency } from "../utils/currency";

const SHOP_CART_KEY = "shop_cart_items";
const SHOP_AFFILIATE_KEY = "shop_affiliate_context";
const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || ""));

const MONTONIO_PAYMENT_METHOD_OPTIONS = [
    { value: "paymentInitiation", label: "Bank payment" },
    { value: "cardPayments", label: "Card payment" },
    { value: "blik", label: "BLIK" },
    { value: "mobilePay", label: "MobilePay" }
];

const ShopCheckoutFlowPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [placingOrder, setPlacingOrder] = useState(false);
    const [error, setError] = useState("");
    const [creditBalance, setCreditBalance] = useState(0);
    const [creditMode, setCreditMode] = useState("partial");
    const [customCredit, setCustomCredit] = useState("");
    const [selectedMontonioPaymentMethod, setSelectedMontonioPaymentMethod] = useState("paymentInitiation");

    const [affiliate] = useState(() => {
        if (location.state?.affiliate?.id) return location.state.affiliate;
        const saved = localStorage.getItem(SHOP_AFFILIATE_KEY);
        try {
            return saved ? JSON.parse(saved) : null;
        } catch (error) {
            return null;
        }
    });

    const [cart, setCart] = useState(() => {
        if (Array.isArray(location.state?.cart) && location.state.cart.length) {
            return location.state.cart;
        }
        const saved = localStorage.getItem(SHOP_CART_KEY);
        try {
            return saved ? JSON.parse(saved) : [];
        } catch (error) {
            return [];
        }
    });

    const paymentCurrency = affiliate?.paymentCurrency || "EUR";
    const formatMoney = (value) => formatCurrency(value, paymentCurrency);
    const isMontonioBankPaymentEnabled = affiliate?.isMontonioBankPaymentEnabled !== false;
    const isMontonioCardPaymentEnabled = affiliate?.isMontonioCardPaymentEnabled === true;
    const isMontonioBlikPaymentEnabled = paymentCurrency === "PLN"
        && affiliate?.isMontonioBlikPaymentEnabled === true;
    const isMobilePayPaymentAvailable = affiliate?.paymentCountry === "FI"
        && paymentCurrency === "EUR"
        && isMontonioCardPaymentEnabled;
    const availableMontonioPaymentMethods = MONTONIO_PAYMENT_METHOD_OPTIONS.filter((method) => (
        (method.value === "paymentInitiation" && isMontonioBankPaymentEnabled)
        || (method.value === "cardPayments" && isMontonioCardPaymentEnabled)
        || (method.value === "blik" && isMontonioBlikPaymentEnabled)
        || (method.value === "mobilePay" && isMobilePayPaymentAvailable)
    ));

    useEffect(() => {
        localStorage.setItem(SHOP_CART_KEY, JSON.stringify(cart));
    }, [cart]);

    useEffect(() => {
        const loadCredit = async () => {
            if (!affiliate?.id) return;
            try {
                const response = await getUserCredit(affiliate.id);
                setCreditBalance(Number(response?.credit || 0));
            } catch (loadError) {
                console.error("Failed to fetch credit:", loadError);
            }
        };
        void loadCredit();
    }, [affiliate?.id]);

    useEffect(() => {
        const availableValues = MONTONIO_PAYMENT_METHOD_OPTIONS
            .filter((method) => (
                (method.value === "paymentInitiation" && isMontonioBankPaymentEnabled)
                || (method.value === "cardPayments" && isMontonioCardPaymentEnabled)
                || (method.value === "blik" && isMontonioBlikPaymentEnabled)
                || (method.value === "mobilePay" && isMobilePayPaymentAvailable)
            ))
            .map((method) => method.value);

        if (!availableValues.includes(selectedMontonioPaymentMethod)) {
            setSelectedMontonioPaymentMethod(availableValues[0] || "paymentInitiation");
        }
    }, [isMontonioBankPaymentEnabled, isMontonioCardPaymentEnabled, isMontonioBlikPaymentEnabled, isMobilePayPaymentAvailable, selectedMontonioPaymentMethod]);

    const subtotal = useMemo(
        () => cart.reduce((sum, item) => sum + Number(item.finalPrice || 0) * Number(item.quantity || 0), 0),
        [cart]
    );

    const creditAmount = useMemo(() => {
        if (creditMode === "none") return 0;
        if (creditMode === "full") return Math.min(subtotal, creditBalance);
        if (customCredit !== "") {
            const parsed = Number(customCredit);
            if (Number.isFinite(parsed) && parsed > 0) {
                return Math.min(parsed, creditBalance, subtotal);
            }
        }
        return Math.min(creditBalance, subtotal);
    }, [creditMode, customCredit, creditBalance, subtotal]);

    const montonioAmount = Math.max(0, Number((subtotal - creditAmount).toFixed(2)));

    const handleQtyChange = (productId, quantity) => {
        setCart((prev) => prev.map((item) => {
            if (item.productId !== productId) return item;
            const nextQty = Number.isFinite(quantity) ? quantity : item.quantity;
            const safeQty = Math.max(1, Math.min(nextQty, item.stockQuantity || nextQty));
            return { ...item, quantity: safeQty };
        }));
    };

    const changeQuantityBy = (productId, delta) => {
        const target = cart.find((item) => item.productId === productId);
        if (!target) return;
        handleQtyChange(productId, target.quantity + delta);
    };

    const handleRemove = (productId) => {
        setCart((prev) => prev.filter((item) => item.productId !== productId));
    };

    const handleBackToShop = () => {
        navigate("/shop-checkout", { state: { affiliate } });
    };

    const redirectSameTab = (url) => {
        if (!url) return;
        if (isAbsoluteUrl(url)) {
            window.location.assign(url);
            return;
        }
        navigate(url);
    };

    const handlePlaceOrder = async () => {
        if (!affiliate?.id) {
            setError("Affiliate context is missing");
            return;
        }
        if (!cart.length) {
            setError("Cart is empty");
            return;
        }
        if (creditMode === "full" && creditBalance < subtotal) {
            setError("Not enough credit for full credit checkout");
            return;
        }

        setError("");
        setPlacingOrder(true);
        try {
            const payload = {
                affiliateId: affiliate.id,
                items: cart.map((item) => ({
                    productId: item.productId,
                    quantity: item.quantity
                }))
            };

            if (creditMode === "full" && montonioAmount === 0) {
                const response = await createCreditShopCheckout(payload);
                localStorage.removeItem(SHOP_CART_KEY);
                localStorage.setItem("shop_last_order_ref", response.merchantReference || "");
                if (response.merchantReference) {
                    localStorage.setItem("shop_pending_order_ref", response.merchantReference);
                }
                const creditReturnUrl = response?.redirectUrl
                    || (response?.merchantReference
                        ? `/after-checkout?shop=1&ref=${encodeURIComponent(response.merchantReference)}`
                        : "/after-checkout?shop=1");
                redirectSameTab(creditReturnUrl);
                return;
            }

            const response = await createMontonioShopCheckout({
                ...payload,
                creditMode,
                creditAmount,
                paymentMethod: selectedMontonioPaymentMethod
            });
            localStorage.setItem("shop_last_order_ref", response.merchantReference || "");
            localStorage.setItem("shop_pending_order_ref", response.merchantReference || "");
            localStorage.removeItem(SHOP_CART_KEY);

            // Backend may auto-convert this to credit checkout when remaining amount is 0.
            if (response?.paymentUrl) {
                window.location.assign(response.paymentUrl);
                return;
            }

            const returnUrl = response?.redirectUrl
                || (response?.merchantReference
                    ? `/after-checkout?shop=1&ref=${encodeURIComponent(response.merchantReference)}`
                    : "/after-checkout?shop=1");
            redirectSameTab(returnUrl);
        } catch (checkoutError) {
            console.error("Shop checkout error:", checkoutError);
            setError(checkoutError?.response?.data?.message || "Could not start checkout");
        } finally {
            setPlacingOrder(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 1.5, md: 3 } }}>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    mb: 2,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    background: "linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,1) 45%)"
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ShoppingBagIcon color="primary" />
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                Checkout
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Review your order and complete payment securely.
                            </Typography>
                        </Box>
                    </Box>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBackToShop}>
                        Back to shop
                    </Button>
                </Box>
            </Paper>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            {!cart.length ? (
                <Alert severity="warning">Cart is empty. Add products in shop first.</Alert>
            ) : (
                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                                    Order items
                                </Typography>
                                {cart.map((item) => (
                                    <Box
                                        key={item.productId}
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: { xs: "1fr", sm: "2fr auto auto" },
                                            gap: 1.5,
                                            alignItems: "center",
                                            py: 1.5,
                                            borderBottom: "1px solid #eee"
                                        }}
                                    >
                                        <Box>
                                            <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                                            {item.giftCardMeta ? (
                                                <Typography variant="caption" color="secondary" sx={{ display: "block" }}>
                                                    Gift card ({item.giftCardMeta.validityDays} days)
                                                </Typography>
                                            ) : null}
                                            <Typography variant="caption" color="text.secondary">
                                                {formatMoney(item.finalPrice)} each
                                            </Typography>
                                        </Box>

                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ justifySelf: { xs: "start", sm: "center" } }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => changeQuantityBy(item.productId, -1)}
                                                disabled={item.quantity <= 1}
                                            >
                                                <RemoveIcon fontSize="small" />
                                            </IconButton>
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Qty"
                                                value={item.quantity}
                                                onChange={(event) => handleQtyChange(item.productId, Number(event.target.value))}
                                                inputProps={{ min: 1, max: item.stockQuantity }}
                                                sx={{ width: 90 }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={() => changeQuantityBy(item.productId, 1)}
                                                disabled={item.quantity >= (item.stockQuantity || item.quantity)}
                                            >
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>

                                        <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                                            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                                                {formatMoney(item.finalPrice * item.quantity)}
                                            </Typography>
                                            <Button size="small" color="error" onClick={() => handleRemove(item.productId)}>
                                                Remove
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card
                            sx={{
                                borderRadius: 3,
                                border: "1px solid",
                                borderColor: "divider",
                                position: { md: "sticky" },
                                top: { md: 84 }
                            }}
                        >
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                                    Order summary
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Available credit: {formatMoney(creditBalance)}
                                </Typography>

                                <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                                    <Chip size="small" label={`${cart.length} items`} />
                                    <Chip size="small" color="primary" label={`Subtotal ${formatMoney(subtotal)}`} />
                                </Box>

                                <Divider sx={{ mb: 2 }} />

                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Payment method
                                </Typography>
                                <FormControl>
                                    <RadioGroup
                                        sx={{ gap: 0.5 }}
                                        value={creditMode}
                                        onChange={(event) => setCreditMode(event.target.value)}
                                    >
                                        <FormControlLabel value="partial" control={<Radio />} label="Use credit first" />
                                        <FormControlLabel value="none" control={<Radio />} label="Do not use credit" />
                                        <FormControlLabel value="full" control={<Radio />} label="Full credit only" />
                                    </RadioGroup>
                                </FormControl>

                                {creditMode === "partial" ? (
                                    <TextField
                                        sx={{ mt: 1.5 }}
                                        label="Custom credit amount (optional)"
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={customCredit}
                                        onChange={(event) => setCustomCredit(event.target.value)}
                                    />
                                ) : null}

                                {montonioAmount > 0 ? (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                            Montonio payment method
                                        </Typography>
                                        {availableMontonioPaymentMethods.length > 1 ? (
                                            <FormControl>
                                                <RadioGroup
                                                    sx={{ gap: 0.5 }}
                                                    value={selectedMontonioPaymentMethod}
                                                    onChange={(event) => setSelectedMontonioPaymentMethod(event.target.value)}
                                                >
                                                    {availableMontonioPaymentMethods.map((method) => (
                                                        <FormControlLabel
                                                            key={method.value}
                                                            value={method.value}
                                                            control={<Radio />}
                                                            label={method.label}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            </FormControl>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {availableMontonioPaymentMethods[0]?.label || "Bank payment"}
                                            </Typography>
                                        )}
                                    </Box>
                                ) : null}

                                <Box sx={{ mt: 2.5, mb: 2 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                        <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                                        <Typography variant="body2">{formatMoney(subtotal)}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                        <Typography variant="body2" color="text.secondary">Credit</Typography>
                                        <Typography variant="body2">-{formatMoney(creditAmount)}</Typography>
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography sx={{ fontWeight: 700, display: "flex", justifyContent: "space-between" }}>
                                        <span>To pay via Montonio</span>
                                        <span>{formatMoney(montonioAmount)}</span>
                                    </Typography>
                                </Box>

                                <Alert
                                    icon={<LockIcon fontSize="inherit" />}
                                    severity="info"
                                    sx={{ mb: 2 }}
                                >
                                    You will be redirected in the same tab to complete payment securely.
                                </Alert>

                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    sx={{ mt: 0.5, py: 1.2, fontWeight: 700 }}
                                    disabled={!cart.length || placingOrder}
                                    onClick={handlePlaceOrder}
                                >
                                    {placingOrder ? "Processing..." : "Confirm and pay"}
                                </Button>

                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, color: "text.secondary" }}>
                                    <VerifiedUserIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="caption">
                                        Order status and stock update are validated server-side.
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* bottom spacer for app menus */}
            <Box sx={{ height: { xs: "calc(90px + env(safe-area-inset-bottom, 0px))", md: 88 } }} />
        </Container>
    );
};

export default ShopCheckoutFlowPage;
